import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import { useEffect, useMemo, useRef, useState } from 'react'

import { PNI_AGENT_MATCH_ERROR_FALLBACK_MESSAGE } from 'components/status-checks/edit/pni-agents-matching-tags-utils'
import { usePostIntegrationClientsMatchesTags } from 'queries/integration-clients/use-post-integration-clients-matches-tags'
import { naturalSortBy, unique } from 'utils/list'
import { omit } from 'utils/obj'
import { messageFromResponseError } from 'utils/strings'

import type { TagData } from 'components/common/AddTagsForm'
import type { MultiValueTags } from 'types/tags'

interface UsePniMultiSelectTagsOptions {
  multiSelectTagsValue: MultiValueTags | undefined
  onLastTagDeleted?: () => void
  onSetValue: (values: MultiValueTags | undefined) => void
  pniOptions: {
    enabled: boolean
    onError?: (message: string) => void
  }
}

interface UsePniMultiSelectTagsResult {
  agentsMatchingTags: string[]
  handleAddTag: (tagData: TagData) => void
  handleDeleteTag: ([tagName]: [string, string[]]) => void
  isLoadingCheckMatchingTags: boolean
  matchingAgentsError: string | undefined
  tagNames: string[]
  tags: MultiValueTags
  tagValues: string[]
}

export function usePniMultiSelectTags({
  multiSelectTagsValue,
  onLastTagDeleted,
  onSetValue,
  pniOptions
}: UsePniMultiSelectTagsOptions): UsePniMultiSelectTagsResult {
  const [agentsMatchingTags, setAgentsMatchingTags] = useState<string[]>([])
  const [matchingAgentsError, setMatchingAgentsError] = useState<
    string | undefined
  >()

  const {
    mutate: checkMatchingTagsMutation,
    isLoading: isLoadingCheckMatchingTags
  } = usePostIntegrationClientsMatchesTags()

  const { tags, tagNames, tagValues } = useMemo(() => {
    const resolvedTags = multiSelectTagsValue ?? {}

    return {
      tags: resolvedTags,
      tagNames: Object.keys(resolvedTags),
      tagValues: unique(Object.values(resolvedTags).flat())
    }
  }, [multiSelectTagsValue])

  const pniEnabled = pniOptions?.enabled
  const pniOnError = pniOptions?.onError

  const tagsRef = useRef(multiSelectTagsValue)

  if (!isEqual(tagsRef.current, multiSelectTagsValue)) {
    tagsRef.current = multiSelectTagsValue
  }

  const stableTags = tagsRef.current

  useEffect(
    function checkMatchingAgentsForTags() {
      if (!pniEnabled) {
        setAgentsMatchingTags([])
        setMatchingAgentsError(undefined)
        return
      }

      checkMatchingTagsMutation(stableTags, {
        onError(error) {
          const message = messageFromResponseError(
            error,
            PNI_AGENT_MATCH_ERROR_FALLBACK_MESSAGE
          )
          setMatchingAgentsError(message)
          pniOnError?.(message)
        },
        onSuccess(data) {
          setAgentsMatchingTags(data.matchingAgents)
          setMatchingAgentsError(undefined)
        }
      })
    },
    [checkMatchingTagsMutation, stableTags, pniEnabled, pniOnError]
  )

  const handleAddTag = ({ name, values }: TagData) => {
    onSetValue({
      ...multiSelectTagsValue,
      [name]: naturalSortBy(values)
    })
  }

  const handleDeleteTag = ([tagName]: [string, string[]]) => {
    const newTags = omit<MultiValueTags>(multiSelectTagsValue ?? {}, tagName)

    if (!isEmpty(newTags)) {
      return onSetValue(newTags)
    }

    // When all tags are deleted, let the caller optionally handle the empty case (e.g. reset form dirty state), otherwise fall back to clearing the value
    const onEmpty = onLastTagDeleted ?? (() => onSetValue(undefined))
    onEmpty()
  }

  return {
    agentsMatchingTags,
    handleAddTag,
    handleDeleteTag,
    isLoadingCheckMatchingTags,
    matchingAgentsError,
    tagNames,
    tags,
    tagValues
  }
}
